import React, { useState, useRef } from 'react';

/**
 * Shared maturity schedule file upload + review table.
 *
 * Props:
 *   columns     — array of { key, label } defining which parsed fields to show
 *   onPopulate  — callback(rows) when user clicks "Populate Calculator"
 */

const sBtn = { padding: '8px 18px', borderRadius: '4px', border: 'none', cursor: 'pointer',
  fontSize: '13px', fontWeight: '600', transition: 'all 0.15s ease' };
const sBtnPrimary = { ...sBtn, background: 'var(--accent-color)', color: '#fff' };
const sBtnSecondary = { ...sBtn, background: 'var(--bg-tertiary)', color: 'var(--text-primary)',
  border: '1px solid var(--border-color)' };
const sTable = { width: '100%', borderCollapse: 'collapse', fontSize: '12px' };
const sTh = { padding: '6px 8px', textAlign: 'right', fontWeight: '600', fontSize: '11px',
  textTransform: 'uppercase', letterSpacing: '0.03em', color: 'var(--text-secondary)',
  borderBottom: '2px solid var(--border-color)', background: 'var(--bg-secondary)' };
const sTd = { padding: '5px 8px', textAlign: 'right', borderBottom: '1px solid var(--border-light)',
  fontSize: '12px' };
const sInput = { width: '100%', padding: '4px 6px', border: '1px solid var(--border-color)',
  borderRadius: '4px', fontSize: '12px', boxSizing: 'border-box', outline: 'none',
  background: 'var(--input-bg)', color: 'var(--text-primary)' };

const MaturityUpload = ({ columns, onPopulate }) => {
  const [file, setFile] = useState(null);
  const [rows, setRows] = useState(null);
  const [warnings, setWarnings] = useState([]);
  const [sourceFormat, setSourceFormat] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const fileRef = useRef();

  const handleFile = (f) => {
    if (!f) return;
    const ext = f.name.split('.').pop().toLowerCase();
    if (ext !== 'xlsx' && ext !== 'pdf') {
      setError('Only .xlsx and .pdf files are supported');
      return;
    }
    setFile(f);
    setError('');
    setRows(null);
    setWarnings([]);
  };

  const handleDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    const f = e.dataTransfer.files[0];
    if (f) handleFile(f);
  };

  const parse = async () => {
    if (!file) return;
    setBusy(true);
    setError('');
    try {
      const form = new FormData();
      form.append('file', file);
      const res = await fetch('/api/parse-maturity-schedule', { method: 'POST', body: form });
      const data = await res.json();
      if (!res.ok) throw new Error(data.detail || 'Parse failed');
      setRows(data.rows || []);
      setWarnings(data.warnings || []);
      setSourceFormat(data.source_format || '');
    } catch (e) {
      setError(e.message);
    } finally {
      setBusy(false);
    }
  };

  const updateCell = (rowIdx, key, value) => {
    setRows(prev => {
      const updated = [...prev];
      updated[rowIdx] = { ...updated[rowIdx], [key]: value };
      return updated;
    });
  };

  const removeRow = (idx) => {
    setRows(prev => prev.filter((_, i) => i !== idx));
  };

  const reset = () => {
    setFile(null);
    setRows(null);
    setWarnings([]);
    setError('');
    setSourceFormat('');
    if (fileRef.current) fileRef.current.value = '';
  };

  return (
    <div style={{ marginBottom: '20px' }}>
      {/* Upload zone */}
      {!rows && (
        <div
          onClick={() => fileRef.current && fileRef.current.click()}
          onDrop={handleDrop}
          onDragOver={(e) => { e.preventDefault(); e.stopPropagation(); }}
          style={{
            border: `2px dashed ${file ? 'var(--accent-color)' : 'var(--border-color)'}`,
            borderRadius: '8px',
            padding: '18px 24px',
            textAlign: 'center',
            cursor: 'pointer',
            background: file ? 'var(--accent-subtle, rgba(74,222,128,0.05))' : 'var(--bg-secondary)',
            transition: 'all 0.15s ease',
            marginBottom: '12px',
          }}
        >
          <input
            ref={fileRef}
            type="file"
            accept=".xlsx,.pdf"
            style={{ display: 'none' }}
            onChange={(e) => { handleFile(e.target.files[0]); e.target.value = ''; }}
          />
          {file ? (
            <div>
              <span style={{ fontWeight: '600', color: 'var(--accent-color)', fontSize: '13px' }}>
                {file.name}
              </span>
              <span style={{ fontSize: '12px', color: 'var(--text-tertiary)', marginLeft: '8px' }}>
                ({(file.size / 1024).toFixed(0)} KB)
              </span>
            </div>
          ) : (
            <div style={{ color: 'var(--text-secondary)', fontSize: '13px' }}>
              Drop an Excel (.xlsx) or PDF file here, or click to browse
            </div>
          )}
        </div>
      )}

      {/* Parse button */}
      {file && !rows && (
        <button onClick={parse} disabled={busy} style={{ ...sBtnPrimary, opacity: busy ? 0.7 : 1 }}>
          {busy ? 'Parsing...' : 'Parse Maturity Schedule'}
        </button>
      )}

      {/* Error */}
      {error && (
        <div style={{ padding: '10px 14px', background: '#fef2f2', border: '1px solid #fecaca',
          borderRadius: '4px', color: '#991b1b', fontSize: '13px', marginTop: '8px' }}>
          {error}
        </div>
      )}

      {/* Warnings */}
      {warnings.length > 0 && (
        <div style={{ padding: '10px 14px', background: '#fffbea', border: '1px solid #f0c040',
          borderRadius: '5px', fontSize: '12px', color: '#7a5c00', marginTop: '8px', marginBottom: '8px' }}>
          <strong>Parser notes:</strong>
          <ul style={{ margin: '4px 0 0 0', paddingLeft: '18px' }}>
            {warnings.map((w, i) => <li key={i}>{w}</li>)}
          </ul>
        </div>
      )}

      {/* Review table */}
      {rows && rows.length > 0 && (
        <div style={{ marginTop: '8px' }}>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '8px' }}>
            <div style={{ fontSize: '12px', color: 'var(--text-secondary)' }}>
              {rows.length} rows parsed from {sourceFormat.toUpperCase()} — review and edit before populating
            </div>
            <button onClick={reset} style={{ ...sBtnSecondary, fontSize: '11px', padding: '4px 12px' }}>Clear</button>
          </div>
          <div style={{ overflowX: 'auto', marginBottom: '10px' }}>
            <table style={sTable}>
              <thead>
                <tr>
                  <th style={{ ...sTh, textAlign: 'center', width: '30px' }}>#</th>
                  {columns.map(col => (
                    <th key={col.key} style={{ ...sTh, textAlign: col.key.includes('date') ? 'left' : 'right' }}>
                      {col.label}
                    </th>
                  ))}
                  <th style={{ ...sTh, width: '32px' }}></th>
                </tr>
              </thead>
              <tbody>
                {rows.map((row, ri) => (
                  <tr key={ri}>
                    <td style={{ ...sTd, textAlign: 'center', color: 'var(--text-tertiary)', fontSize: '11px' }}>
                      {ri + 1}
                    </td>
                    {columns.map(col => (
                      <td key={col.key} style={{ ...sTd, textAlign: col.key.includes('date') ? 'left' : 'right' }}>
                        <input
                          type="text"
                          value={row[col.key] != null ? String(row[col.key]) : ''}
                          onChange={(e) => updateCell(ri, col.key, e.target.value)}
                          style={{ ...sInput, textAlign: col.key.includes('date') ? 'left' : 'right',
                            width: col.key.includes('date') ? '110px' : '100px' }}
                        />
                      </td>
                    ))}
                    <td style={{ ...sTd, textAlign: 'center' }}>
                      <button onClick={() => removeRow(ri)} title="Remove row"
                        style={{ background: 'none', border: 'none', color: 'var(--error-color)',
                          cursor: 'pointer', fontSize: '14px', lineHeight: 1 }}>x</button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <div style={{ display: 'flex', gap: '8px' }}>
            <button onClick={() => { onPopulate(rows); reset(); }} style={sBtnPrimary}>
              Populate Calculator
            </button>
            <button onClick={reset} style={sBtnSecondary}>Cancel</button>
          </div>
        </div>
      )}

      {rows && rows.length === 0 && (
        <div style={{ padding: '10px 14px', background: '#fef2f2', border: '1px solid #fecaca',
          borderRadius: '4px', color: '#991b1b', fontSize: '13px', marginTop: '8px' }}>
          No maturity rows found in the uploaded file. Try a different file or enter data manually.
          <button onClick={reset} style={{ ...sBtnSecondary, marginLeft: '12px', fontSize: '11px', padding: '4px 10px' }}>
            Try Again
          </button>
        </div>
      )}
    </div>
  );
};

export default MaturityUpload;
